import { createTheme } from '@mui/material/styles';
import { color, radius, shadow, font } from './tokens';

// Centralised MUI theme. All component styling decisions live here so the
// pages stay declarative and consistent.
export const appTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: color.primary[500],
      dark: color.primary[700],
      light: color.primary[400],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: color.yellow[500],
      dark: color.yellow[600],
      light: color.yellow[400],
      contrastText: '#111827',
    },
    success: { main: color.success.solid, contrastText: '#FFFFFF' },
    warning: { main: color.warning.solid, contrastText: '#FFFFFF' },
    error:   { main: color.danger.solid,  contrastText: '#FFFFFF' },
    info:    { main: color.info.solid,    contrastText: '#FFFFFF' },
    text: {
      primary: color.ink[900],
      secondary: color.ink[500],
      disabled: color.ink[400],
    },
    background: { default: color.bg, paper: color.surface },
    divider: color.divider,
  },

  shape: { borderRadius: radius.md },

  typography: {
    fontFamily: font,
    // Display / headings — Poppins 600 keeps the brand voice without shouting
    h1: { fontSize: '1.5rem',   fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.015em', color: color.ink[900] },
    h2: { fontSize: '1.25rem',  fontWeight: 600, lineHeight: 1.3,  letterSpacing: '-0.01em',  color: color.ink[900] },
    h3: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35, color: color.ink[900] },
    h4: { fontSize: '1rem',     fontWeight: 600, lineHeight: 1.4,  color: color.ink[900] },
    h5: { fontSize: '0.9375rem',fontWeight: 600, lineHeight: 1.4,  color: color.ink[900] },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4,  color: color.ink[900] },
    subtitle1: { fontSize: '0.875rem',  fontWeight: 500, color: color.ink[800] },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600, color: color.ink[700] },
    body1: { fontSize: '0.875rem',  fontWeight: 400, lineHeight: 1.55, color: color.ink[700] },
    body2: { fontSize: '0.875rem',  fontWeight: 400, lineHeight: 1.5,  color: color.ink[600] },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4, color: color.ink[500] },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1.6 },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: color.bg, color: color.ink[700] },
        '::selection': { backgroundColor: color.primary[100] },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: radius.xl },
        outlined: { borderColor: color.border },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.xl,
          border: `1px solid ${color.border}`,
          boxShadow: shadow.xs,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
    },
    MuiCardActions: {
      styleOverrides: { root: { padding: '12px 20px 16px' } },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          paddingInline: 16,
          minHeight: 38,
          '&:focus-visible': { boxShadow: shadow.focusGold },
        },
        sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: '0.8125rem' },
        sizeLarge: { minHeight: 44, fontSize: '0.9375rem' },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        containedPrimary: {
          backgroundColor: color.primary[500],
          color: color.gold.ink,
          '&:hover': { backgroundColor: color.gold[600] },
          '&:active': { backgroundColor: color.gold[700] },
        },
        containedSecondary: {
          backgroundColor: color.ink[900],
          '&:hover': { backgroundColor: color.ink[800] },
        },
        outlined: {
          borderColor: color.borderStrong,
          color: color.ink[700],
          '&:hover': { borderColor: color.ink[400], backgroundColor: color.ink[50] },
        },
        text: {
          color: color.ink[700],
          '&:hover': { backgroundColor: color.ink[100] },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          color: color.ink[500],
          '&:hover': { backgroundColor: color.ink[100], color: color.ink[800] },
        },
        sizeSmall: { padding: 6 },
      },
    },

    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: radius.md } },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: color.surface,
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: color.borderStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color.ink[400] },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: color.primary[500],
            borderWidth: 2,
            boxShadow: shadow.focusGold,
          },
        },
        input: { padding: '10px 12px', color: color.ink[900] },
        inputSizeSmall: { padding: '8px 12px' },
        inputMultiline: { padding: '10px 12px' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.875rem', color: color.ink[600] },
        shrink: { fontWeight: 500 },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { color: color.ink[500], marginLeft: 2 } },
    },
    MuiSelect: {
      styleOverrides: { select: { fontSize: '0.875rem' } },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          border: `1px solid ${color.border}`,
          boxShadow: shadow.md,
          marginTop: 6,
          minWidth: 200,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: color.ink[700],
          borderRadius: radius.sm,
          margin: '2px 6px',
          paddingBlock: 8,
          '&:hover': { backgroundColor: color.ink[100] },
        },
      },
    },

    MuiDialog: {
      styleOverrides: { paper: { borderRadius: radius['2xl'], border: `1px solid ${color.border}` } },
    },
    MuiBackdrop: {
      styleOverrides: { root: { backgroundColor: 'rgba(16,24,40,0.45)', backdropFilter: 'blur(2px)' } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1rem', fontWeight: 600, padding: '20px 24px', color: color.ink[900] } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: '4px 24px 20px' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '16px 24px 20px', gap: 8 } },
    },

    MuiTable: {
      styleOverrides: { root: { borderCollapse: 'separate', borderSpacing: 0 } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: color.ink[50],
            color: color.ink[500],
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${color.border}`,
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${color.divider}`,
          fontSize: '0.875rem',
          color: color.ink[700],
          padding: '12px 16px',
        },
        sizeSmall: { padding: '8px 12px' },
        head: { borderBottom: `1px solid ${color.border}` },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:last-child .MuiTableCell-root': { borderBottom: 'none' } },
        hover: { '&:hover': { backgroundColor: color.ink[50] } },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { backgroundColor: color.primary[500], height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          minHeight: 44,
          color: color.ink[500],
          '&.Mui-selected': { color: color.ink[900], fontWeight: 600 },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 24,
        },
        sizeSmall: { height: 22, fontSize: '0.6875rem' },
        filledPrimary: { backgroundColor: color.primary[100], color: color.gold[700] },
        outlined: { borderColor: color.borderStrong },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: color.ink[900],
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: radius.sm,
          padding: '6px 10px',
        },
        arrow: { color: color.ink[900] },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.lg, fontSize: '0.875rem', alignItems: 'center' },
        standardError:   { backgroundColor: color.danger.bg,  color: color.danger.text,  border: `1px solid ${color.danger.border}` },
        standardSuccess: { backgroundColor: color.success.bg, color: color.success.text, border: `1px solid ${color.success.border}` },
        standardWarning: { backgroundColor: color.warning.bg, color: color.warning.text, border: `1px solid ${color.warning.border}` },
        standardInfo:    { backgroundColor: color.info.bg,    color: color.info.text,    border: `1px solid ${color.info.border}` },
      },
    },

    MuiSnackbarContent: {
      styleOverrides: {
        root: { borderRadius: radius.lg, boxShadow: shadow.lg, fontSize: '0.875rem' },
      },
    },

    MuiPagination: {
      styleOverrides: {
        root: { '& .MuiPaginationItem-root': { borderRadius: radius.md, fontSize: '0.875rem', fontWeight: 500 } },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: color.primary[100],
            color: color.gold[700],
            borderColor: color.primary[100],
            '&:hover': { backgroundColor: color.primary[100] },
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        switchBase: {
          '&.Mui-checked': {
            color: color.primary[500],
            '& + .MuiSwitch-track': { backgroundColor: color.primary[500], opacity: 1 },
          },
        },
        track: { backgroundColor: color.ink[300], opacity: 1 },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: color.divider } },
    },

    MuiSkeleton: {
      styleOverrides: { root: { backgroundColor: color.ink[100] }, text: { borderRadius: radius.sm } },
    },

    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: radius.pill, backgroundColor: color.ink[100] } },
    },

    MuiAvatar: {
      styleOverrides: { root: { fontSize: '0.8125rem', fontWeight: 600 } },
    },
  },
});

export default appTheme;
